import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import {
  ArrowLeft,
  Play,
  Square,
  RotateCcw,
  Globe,
  Puzzle,
  Layers,
  Info,
  BarChart3,
  Github,
} from 'lucide-react';
import { api } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { ContainerStatusBadge } from '@/components/StatusBadge';
import type { Site } from '@/types';
import { OverviewTab } from './OverviewTab';
import { DomainsTab } from './DomainsTab';
import { WidgetsTab } from './WidgetsTab';
import { DeploymentsTab } from './DeploymentsTab';
import { AnalyticsTab } from './AnalyticsTab';
import { GitHubTab } from './GitHubTab';

type Tab = 'overview' | 'domains' | 'widgets' | 'deployments' | 'analytics' | 'github';

export function SiteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('overview');
  const [actionLoading, setActionLoading] = useState('');

  const fetchSite = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<Site>(`/sites/${id}`);
      setSite(data);
    } catch {
      /* error boundary */
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchSite();
  }, [fetchSite]);

  const handleAction = async (action: 'deploy' | 'undeploy' | 'restart') => {
    if (!id) return;
    setActionLoading(action);
    try {
      await api.post(`/sites/${id}/${action}`);
      await fetchSite();
    } catch {
      /* toast */
    } finally {
      setActionLoading('');
    }
  };

  if (loading || !site) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'overview', label: 'Übersicht', icon: <Info className="h-4 w-4" /> },
    { key: 'domains', label: 'Domains', icon: <Globe className="h-4 w-4" /> },
    { key: 'widgets', label: 'Widgets', icon: <Puzzle className="h-4 w-4" /> },
    { key: 'deployments', label: 'Deployments', icon: <Layers className="h-4 w-4" /> },
    { key: 'analytics', label: 'Analytics', icon: <BarChart3 className="h-4 w-4" /> },
    ...(site.repoUrl ? [{ key: 'github' as Tab, label: 'Repository', icon: <Github className="h-4 w-4" /> }] : []),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/sites"
          className="mb-3 inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-300 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück zu Sites
        </Link>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-100">{site.name}</h1>
            <p className="mt-1 text-sm text-neutral-500 font-mono">{site.identifier}</p>
          </div>

          <div className="flex items-center gap-2">
            <ContainerStatusBadge status={site.containerStatus} />

            {isAdmin && (
              <>
                {(site.containerStatus === 'STOPPED' || site.containerStatus === 'PENDING' || site.containerStatus === 'ERROR') && (
                  <button
                    type="button"
                    onClick={() => handleAction('deploy')}
                    disabled={!!actionLoading}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    <Play className="h-3.5 w-3.5" />
                    {actionLoading === 'deploy' ? 'Deploying…' : 'Deploy'}
                  </button>
                )}
                {site.containerStatus === 'RUNNING' && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleAction('restart')}
                      disabled={!!actionLoading}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-yellow-600 px-3 py-2 text-xs font-medium text-white hover:bg-yellow-700 disabled:opacity-50 transition-colors"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      {actionLoading === 'restart' ? 'Restarting…' : 'Restart'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAction('undeploy')}
                      disabled={!!actionLoading}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      <Square className="h-3.5 w-3.5" />
                      {actionLoading === 'undeploy' ? 'Stopping…' : 'Stop'}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-surface-300">
        {tabs.map(({ key, label, icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
              tab === key
                ? 'border-brand-500 text-brand-400'
                : 'border-transparent text-neutral-500 hover:text-neutral-300'
            }`}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'overview' && <OverviewTab site={site} isAdmin={isAdmin} />}
      {tab === 'domains' && <DomainsTab siteId={site.id} isAdmin={isAdmin} />}
      {tab === 'widgets' && <WidgetsTab siteId={site.id} isAdmin={isAdmin} />}
      {tab === 'deployments' && <DeploymentsTab siteId={site.id} />}
      {tab === 'analytics' && <AnalyticsTab siteId={site.id} />}
      {tab === 'github' && <GitHubTab siteId={site.id} />}
    </div>
  );
}
